import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Send, Check, AlertCircle, Loader2, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", message: "" };

const ContactFormModal = ({ open, onClose }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { ok: bool, msg: string, emailSent?: bool }
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email address.";
    if (form.message.trim().length < 10)
      e.message = "Please write at least a few words about your case.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setResult(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      setResult({
        ok: true,
        emailSent: data.email_sent,
        msg: data.message || "Thank you – your message has been received.",
      });
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again or call directly.";
      setResult({
        ok: false,
        msg: typeof detail === "string" ? detail : "Submission failed.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="contact-modal"
      className="fixed inset-0 z-[100] flex items-end md:items-center justify-center p-0 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
    >
      <div
        className="absolute inset-0 bg-[#0d0c0b]/85 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative w-full md:max-w-2xl bg-[#1A1918] border border-[#C89D54]/40 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] max-h-[95vh] overflow-y-auto">
        <button
          data-testid="contact-modal-close"
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 flex items-center justify-center text-[#A6A098] hover:text-[#C89D54] hover:bg-[#C89D54]/10 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className="px-7 md:px-10 pt-9 pb-2 border-b border-[#C89D54]/15">
          <div className="overline mb-2">Send a Message</div>
          <h3
            id="contact-modal-title"
            className="font-serif-display text-3xl md:text-4xl text-[#F4F0EB] tracking-tight"
          >
            Write to Michael directly.
          </h3>
          <p className="mt-3 text-sm text-[#A6A098] leading-relaxed pb-5">
            Fill in your details and your message will land in Michael&apos;s
            inbox. He usually responds within a few hours.
          </p>
        </div>

        {result?.ok ? (
          <div
            data-testid="contact-success"
            className="px-7 md:px-10 py-12 text-center"
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-[#C89D54]/15 border border-[#C89D54]/50 flex items-center justify-center">
              <Check size={24} className="text-[#C89D54]" strokeWidth={2.2} />
            </div>
            <h4 className="mt-5 font-serif-display text-2xl text-[#F4F0EB]">
              Message sent
            </h4>
            <p className="mt-3 text-sm text-[#A6A098] max-w-md mx-auto leading-relaxed">
              {result.msg}
            </p>
            {!result.emailSent && (
              <p className="mt-3 text-xs text-[#A6A098]/80 italic max-w-md mx-auto">
                Your message has been recorded and Michael will see it. For an
                urgent matter you can also call directly.
              </p>
            )}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                data-testid="contact-success-close"
                onClick={onClose}
                className="btn-secondary"
              >
                Close
              </button>
              <a
                href="tel:+447810620113"
                className="btn-primary"
              >
                <Phone size={14} /> Call 07810 620113
              </a>
            </div>
          </div>
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="px-7 md:px-10 py-7 space-y-5"
            noValidate
          >
            <Field
              testId="contact-field-name"
              label="Your name"
              required
              value={form.name}
              onChange={(v) => update("name", v)}
              error={errors.name}
              placeholder="Jane Smith"
              autoFocus
            />

            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                testId="contact-field-email"
                label="Email address"
                type="email"
                required
                value={form.email}
                onChange={(v) => update("email", v)}
                error={errors.email}
                placeholder="you@example.com"
              />
              <Field
                testId="contact-field-phone"
                label="Phone (optional)"
                type="tel"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                placeholder="07…"
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="overline block mb-2"
              >
                Your message
              </label>
              <textarea
                id="message"
                data-testid="contact-field-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                placeholder="Briefly describe your situation — court, hearing dates, what kind of help you need…"
                className="w-full bg-[#262321] border border-[#C89D54]/20 text-[#F4F0EB] placeholder:text-[#A6A098]/50 px-4 py-3 text-sm font-sans-body focus:outline-none focus:border-[#C89D54] transition-colors resize-none"
              />
              {errors.message && (
                <p className="mt-1.5 text-xs text-[#E08F73] flex items-center gap-1.5">
                  <AlertCircle size={12} /> {errors.message}
                </p>
              )}
            </div>

            {result && !result.ok && (
              <div
                data-testid="contact-error"
                className="flex items-start gap-3 border border-[#E08F73]/50 bg-[#E08F73]/10 px-4 py-3 text-sm text-[#F4F0EB]"
              >
                <AlertCircle size={16} className="text-[#E08F73] shrink-0 mt-0.5" />
                <span>{result.msg}</span>
              </div>
            )}

            <p className="text-xs text-[#A6A098]/80 leading-relaxed">
              Your details will only be used to respond to your enquiry. You
              can also call{" "}
              <a
                href="tel:+447810620113"
                className="text-[#C89D54] hover:underline"
              >
                07810 620113
              </a>{" "}
              or email{" "}
              <a
                href="mailto:Michaelskierniewski@gmail.com"
                className="text-[#C89D54] hover:underline"
              >
                Michaelskierniewski@gmail.com
              </a>
              .
            </p>

            <div className="pt-2 flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                data-testid="contact-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send size={16} /> Send Message
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({
  testId,
  label,
  type = "text",
  value,
  onChange,
  error,
  placeholder,
  required,
  autoFocus,
}) => (
  <div>
    <label className="overline block mb-2">
      {label} {required && <span className="text-[#C89D54]">*</span>}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      className={`w-full bg-[#262321] border ${
        error ? "border-[#E08F73]/60" : "border-[#C89D54]/20"
      } text-[#F4F0EB] placeholder:text-[#A6A098]/50 px-4 py-3 text-sm font-sans-body focus:outline-none focus:border-[#C89D54] transition-colors`}
    />
    {error && (
      <p className="mt-1.5 text-xs text-[#E08F73] flex items-center gap-1.5">
        <AlertCircle size={12} /> {error}
      </p>
    )}
  </div>
);

export default ContactFormModal;
